import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { formatDateTime } from '../utils/dateFormat';
import {
  FileText,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  X,
} from 'lucide-react';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
} from '../utils/animations';

interface Review {
  id: string;
  content: string;
  rating: number;
  created_at: string;
  patient_id: string;
  profiles: {
    prenume: string;
    nume: string;
  } | null;
}

interface Profile {
  id: string;
  role: string;
}

interface DeleteConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  letter: MedicalLetter;
  onSign: () => void;
  currentDoctorId: string;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

interface PrepareForSigningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function PrepareForSigningModal({
  isOpen,
  onClose,
  onConfirm,
}: PrepareForSigningModalProps) {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 text-amber-500">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Atenție
            </h3>
            <p className="text-gray-600 mb-6">
              Câmpurile din scrisoarea medicală nu se vor mai putea edita odată
              ce scrisoarea medicală este pregătită pentru semnare. Ești sigur
              că ai verificat toate câmpurile și nu mai dorești alte modificări?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition"
              >
                Anulează
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition"
              >
                Da
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 text-amber-500">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmare generare scrisoare medicală
            </h3>
            <p className="text-gray-600 mb-6">
              Scrisoarea medicală va fi trimisă automat pe e-mail către pacient
              și doctorii care au semnat pentru a aplica parafa. Ulterior
              generării nu se vor mai putea adăuga alte parafe. Te rog să
              confirmi că dorești să generezi scrisoarea medicală și să închidem
              cazul pacientului.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition"
              >
                Anulează
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-[#007FFF] text-white rounded-lg hover:bg-[#024994] transition"
              >
                Confirmă
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SignatureModal({
  isOpen,
  onClose,
  letter,
  onSign,
  currentDoctorId,
}: SignatureModalProps) {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchDoctors();
    }
  }, [isOpen]);

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, prenume, nume')
        .eq('role', 'doctor')
        .order('nume', { ascending: true })
        .order('prenume', { ascending: true });

      if (error) throw error;
      setDoctors(data || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-[600px] flex flex-col"
        style={{ height: '500px' }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Parafarea scrisorii medicale - {letter.signed_by?.length || 0}{' '}
              parafe
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007FFF] mx-auto"></div>
                <p className="mt-2 text-gray-600">Se încarcă...</p>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto h-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nume și prenume
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {doctors.map((doctor) => {
                    const hasSigned = letter.signed_by?.includes(doctor.id);
                    const isCurrentDoctor = doctor.id === currentDoctorId;

                    return (
                      <tr
                        key={doctor.id}
                        className={`${
                          hasSigned ? 'bg-green-50' : ''
                        } hover:bg-gray-50 transition-colors`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {doctor.nume} {doctor.prenume}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {hasSigned ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Semnat
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Nesemnat
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          {isCurrentDoctor && !hasSigned && (
                            <button
                              onClick={onSign}
                              className="text-[#007FFF] hover:text-[#024994] transition"
                            >
                              Aplică parafa
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Închide
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

interface Doctor {
  id: string;
  prenume: string;
  nume: string;
}

interface Patient {
  id: string;
  prenume: string;
  nume: string;
  email?: string;
  telefon?: string;
  cnp?: string;
  planned_date?: string;
  meeting_url?: string;
  consultation_status?: string;
}

interface MedicalLetter {
  id: string;
  patient_id: string;
  diagnostic: string;
  epicriza: string;
  recomandari: string;
  notes4doctor: string;
  created_at: string;
  closed4editing?: 'closed';
  signed_by?: string[];
  patient: {
    prenume: string;
    nume: string;
    cnp: string;
    consultation_status: string;
    planned_date: string | null;
  };
}

const allowedEmails = ['ovidiu.mudura@yahoo.com', 'tudorbolos2001@yahoo.com'];

export default function MedicalLetters() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [letters, setLetters] = useState<MedicalLetter[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPrepareForSigning, setShowPrepareForSigning] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<MedicalLetter | null>(
    null
  );
  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);
  const [currentDoctorId, setCurrentDoctorId] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUserEmail(user.email ?? null);

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (!profile || profile.role !== 'doctor') {
        navigate('/');
        return;
      }

      setCurrentDoctorId(user.id);
      await fetchLetters();
    } catch (error) {
      console.error('Error checking auth:', error);
      navigate('/login');
    }
  };

  const fetchLetters = async () => {
    try {
      const { data: letters, error } = await supabase
        .from('medical_letters')
        .select(
          `
          *,
          patient:profiles!medical_letters_patient_id_fkey (
            prenume,
            nume,
            cnp,
            consultation_status,
            planned_date
          )
        `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter letters to only include those where:
      // 1. Patient has 'planned' status
      // 2. Planned date exists and is in the past
      const now = new Date();
      const filteredLetters =
        letters?.filter(
          (letter) =>
            letter.patient.consultation_status === 'planned' &&
            letter.patient.planned_date &&
            new Date(letter.patient.planned_date) < now
        ) || [];

      setLetters(filteredLetters);
    } catch (error) {
      console.error('Error fetching letters:', error);
      setError('Error fetching medical letters');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (letter: MedicalLetter) => {
    if (generatingId) return;
    setSelectedLetterId(letter.id);
    setShowConfirmation(true);
  };

  const handleConfirmGenerate = async () => {
    if (!selectedLetterId) return;
    const letter = letters.find((l) => l.id === selectedLetterId);
    if (!letter) return;

    setGeneratingId(selectedLetterId);
    setShowConfirmation(false);
    setSuccessMessage(null);
    setError(null);

    try {
      // Get the patient details including planned_date
      const { data: patient } = await supabase
        .from('profiles')
        .select(
          'prenume, nume, cnp, email, created_at, serviciu, telefon, planned_date'
        )
        .eq('id', letter.patient_id)
        .single();

      if (!patient) throw new Error('Patient not found');

      // Get all doctors who have signed the letter
      const signedDoctors = letter.signed_by || [];

      // Get details for all signed doctors
      const { data: doctors } = await supabase
        .from('profiles')
        .select('id, prenume, nume, email, cnp')
        .in('id', signedDoctors);

      if (!doctors?.length) throw new Error('Scrisoarea nu are aplicată nici măcar o parafă');

      // Transform doctors array to numbered object
      const numberedDoctors = doctors.reduce((
        acc: Record<string, { prenume: string; nume: string; email: string; cnp: string }>, 
        doctor: { prenume: string; nume: string; email: string; cnp: string }, 
        index: number
      ) => {
        acc[`doctor_${index + 1}`] = {
          prenume: doctor.prenume,
          nume: doctor.nume,
          email: doctor.email,
          cnp: doctor.cnp,
        };
        return acc;
      }, {} as Record<string, { prenume: string; nume: string; email: string; cnp: string }>);

      // Add number of stamps
      const webhookData = {
        patient: {
          ...patient,
          planned_date: patient.planned_date
            ? new Date(patient.planned_date).toISOString()
            : null,
        },
        medical_letter: {
          diagnostic: letter.diagnostic || '',
          epicriza: letter.epicriza || '',
          recomandari: letter.recomandari || '',
        },
        ...numberedDoctors,
        number_of_stamps: doctors.length,
      };

      // Send webhook and wait for response
      const response = await fetch(
        'https://n8n.ovi.net.eu.org/webhook/genereaza-scrisoare',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookData),
        }
      );

      if (!response.ok) {
        throw new Error('Eroare la generarea scrisorii medicale cauzată de lipsa unei parafe');
      }

      // Update patient status only after successful PDF generation
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ consultation_status: 'closed' })
        .eq('id', letter.patient_id);

      if (updateError) throw updateError;

      setSuccessMessage(
        'Scrisoarea medicală a fost trimisă pe e-mail la pacient și doctorii care au semnat-o'
      );
      setTimeout(() => setSuccessMessage(null), 5000);

      // Refresh the letters list after successful generation
      await fetchLetters();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'A apărut o eroare la generarea PDF-ului'
      );
    } finally {
      setGeneratingId(null);
    }
  };

  const handleSignLetter = async () => {
    if (!selectedLetter || !currentDoctorId) return;

    try {
      const newSignedBy = [
        ...(selectedLetter.signed_by || []),
        currentDoctorId,
      ];

      const { error } = await supabase
        .from('medical_letters')
        .update({ signed_by: newSignedBy })
        .eq('id', selectedLetter.id);

      if (error) throw error;

      setShowSignatureModal(false);
      await fetchLetters();
    } catch (error) {
      console.error('Error signing letter:', error);
      setError('Error signing letter');
    }
  };

  const handlePrepareForSigning = async () => {
    if (!selectedLetter) return;

    try {
      const { error } = await supabase
        .from('medical_letters')
        .update({ closed4editing: 'closed' })
        .eq('id', selectedLetter.id);

      if (error) throw error;

      setShowPrepareForSigning(false);
      await fetchLetters();
    } catch (error) {
      console.error('Error preparing letter for signing:', error);
      setError('Error preparing letter for signing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-8 w-full overflow-x-hidden overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-xl sm:text-2xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            Scrisori Medicale
          </motion.h1>

          <AnimatePresence>
            {error && (
              <motion.div
                className="bg-red-50 border-l-4 border-red-500 p-4 mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <p className="text-red-700 text-sm sm:text-base">{error}</p>
              </motion.div>
            )}

            {successMessage && (
              <motion.div
                className="bg-green-50 border-l-4 border-green-500 p-4 mb-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <p className="text-green-700 text-sm sm:text-base">
                  {successMessage}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            className="space-y-4"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px' }}
            variants={fastStaggerContainer}
          >
            {letters.length > 0 ? (
              letters.map((letter) => (
                <motion.div
                  key={letter.id}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition"
                  variants={scaleIn}
                  whileHover={{ y: -3, transition: { duration: 0.3 } }}
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {letter.patient.prenume} {letter.patient.nume}
                      </h3>
                      <p className="text-sm text-gray-600">
                        CNP: {letter.patient.cnp}
                      </p>
                      <p className="text-sm text-gray-600">
                        Data: {formatDateTime(letter.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => handleGeneratePDF(letter)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-md transition ${
                          !userEmail ||
                          !allowedEmails.includes(userEmail) ||
                          generatingId === letter.id
                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                            : 'bg-[#007FFF] text-white hover:bg-[#024994]'
                        }`}
                        disabled={
                          !userEmail ||
                          !allowedEmails.includes(userEmail) ||
                          generatingId === letter.id
                        }
                      >
                        {generatingId === letter.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        <span className="whitespace-nowrap">
                          {generatingId === letter.id
                            ? 'Se generează...'
                            : 'Generează scrisoare'}
                        </span>
                      </button>
                      {!letter.closed4editing && (
                        <button
                          onClick={() => {
                            setSelectedLetter(letter);
                            setShowPrepareForSigning(true);
                          }}
                          className={`flex items-center gap-2 px-3 py-1 rounded-md transition whitespace-nowrap ${
                            !userEmail || !allowedEmails.includes(userEmail)
                              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                          disabled={
                            !userEmail || !allowedEmails.includes(userEmail)
                          }
                        >
                          Pregătește pentru semnare
                        </button>
                      )}
                      {letter.closed4editing && (
                        <button
                          onClick={() => {
                            setSelectedLetter(letter);
                            setShowSignatureModal(true);
                          }}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition whitespace-nowrap"
                        >
                          Parafe scrisoare
                        </button>
                      )}
                      <button
                        onClick={() =>
                          navigate(
                            `/doctor/patient/${letter.patient_id}/documents`
                          )
                        }
                        className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-md text-sm hover:bg-gray-200 transition whitespace-nowrap"
                      >
                        <FileText className="w-4 h-4" />
                        Vezi documentele
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">
                Nu există scrisori medicale
              </p>
            )}
          </motion.div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showConfirmation && (
          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => {
              setShowConfirmation(false);
              setSelectedLetterId(null);
            }}
            onConfirm={handleConfirmGenerate}
          />
        )}

        {showPrepareForSigning && selectedLetter && (
          <PrepareForSigningModal
            isOpen={showPrepareForSigning}
            onClose={() => setShowPrepareForSigning(false)}
            onConfirm={handlePrepareForSigning}
          />
        )}

        {showSignatureModal && selectedLetter && currentDoctorId && (
          <SignatureModal
            isOpen={showSignatureModal}
            onClose={() => setShowSignatureModal(false)}
            letter={selectedLetter}
            onSign={handleSignLetter}
            currentDoctorId={currentDoctorId}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
